import React, { useState, useEffect } from "react";
import axios from "axios";
import { useToast } from "@/hooks/use-toast";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Vordefinierte Farbpaletten
const COLOR_PRESETS = [
  { name: "Violett (Standard)", active: "#7c3aed", hover: "#ede9fe", hoverText: "#6d28d9" },
  { name: "Blau", active: "#2563eb", hover: "#dbeafe", hoverText: "#1d4ed8" },
  { name: "Dunkelblau", active: "#1e40af", hover: "#dbeafe", hoverText: "#1e3a8a" },
  { name: "Grün", active: "#059669", hover: "#d1fae5", hoverText: "#047857" },
  { name: "Türkis", active: "#0891b2", hover: "#cffafe", hoverText: "#0e7490" },
  { name: "Rot", active: "#dc2626", hover: "#fee2e2", hoverText: "#b91c1c" },
  { name: "Orange", active: "#ea580c", hover: "#ffedd5", hoverText: "#c2410c" },
  { name: "Pink", active: "#db2777", hover: "#fce7f3", hoverText: "#be185d" },
  { name: "Grau", active: "#475569", hover: "#f1f5f9", hoverText: "#334155" },
  { name: "Schwarz", active: "#1e293b", hover: "#f1f5f9", hoverText: "#0f172a" },
];

export function NavigationColorSettings({ authToken }) {
  const { toast } = useToast();
  const [settings, setSettings] = useState({
    active_bg_color: "#7c3aed",
    active_text_color: "#ffffff",
    hover_bg_color: "#ede9fe",
    hover_text_color: "#6d28d9",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${authToken}` },
  });

  useEffect(() => {
    loadSettings();
  }, [authToken]);

  const loadSettings = async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/navigation-settings`, authHeaders());
      setSettings({
        active_bg_color: res.data.active_bg_color || "#7c3aed",
        active_text_color: res.data.active_text_color || "#ffffff",
        hover_bg_color: res.data.hover_bg_color || "#ede9fe",
        hover_text_color: res.data.hover_text_color || "#6d28d9",
      });
    } catch (e) {
      console.error("Fehler beim Laden der Navigationseinstellungen:", e);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/api/navigation-settings`, settings, authHeaders());
      
      // CSS-Variablen aktualisieren
      document.documentElement.style.setProperty("--nav-active-bg", settings.active_bg_color);
      document.documentElement.style.setProperty("--nav-active-text", settings.active_text_color);
      document.documentElement.style.setProperty("--nav-hover-bg", settings.hover_bg_color);
      document.documentElement.style.setProperty("--nav-hover-text", settings.hover_text_color);
      
      toast({ title: "Navigationsfarben gespeichert" });
    } catch (e) {
      console.error("Fehler beim Speichern:", e);
      toast({ title: "Fehler beim Speichern", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = (preset) => {
    setSettings({
      active_bg_color: preset.active,
      active_text_color: "#ffffff",
      hover_bg_color: preset.hover,
      hover_text_color: preset.hoverText,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Vorschau */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-sm font-medium text-slate-600 mb-3">Vorschau</p>
        <div className="flex flex-wrap gap-2 bg-white p-3 rounded-lg">
          <button
            className="rounded-full px-4 py-2 text-sm font-semibold transition-all"
            style={{
              backgroundColor: settings.active_bg_color,
              color: settings.active_text_color,
            }}
          >
            Aktiver Tab
          </button>
          <button
            className="rounded-full px-4 py-2 text-sm font-semibold transition-all"
            style={{
              backgroundColor: settings.hover_bg_color,
              color: settings.hover_text_color,
            }}
          >
            Hover Tab
          </button>
          <button className="rounded-full px-4 py-2 text-sm font-semibold bg-slate-100 text-slate-600">
            Inaktiver Tab
          </button>
        </div>
      </div>

      {/* Schnellauswahl */}
      <div>
        <p className="text-sm font-medium text-slate-700 mb-3">Schnellauswahl</p>
        <div className="grid grid-cols-5 gap-2">
          {COLOR_PRESETS.map((preset) => (
            <button
              key={preset.name}
              onClick={() => applyPreset(preset)}
              className="group relative flex flex-col items-center gap-1 rounded-lg p-2 hover:bg-slate-50 transition-all"
              title={preset.name}
            >
              <div
                className="h-8 w-8 rounded-full ring-2 ring-offset-2 ring-transparent group-hover:ring-slate-300 transition-all"
                style={{ backgroundColor: preset.active }}
              />
              <span className="text-xs text-slate-500 truncate max-w-full">{preset.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Manuelle Farbauswahl */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Aktiv: Hintergrund
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={settings.active_bg_color}
              onChange={(e) => setSettings({ ...settings, active_bg_color: e.target.value })}
              className="h-10 w-14 cursor-pointer rounded border-0"
            />
            <input
              type="text"
              value={settings.active_bg_color}
              onChange={(e) => setSettings({ ...settings, active_bg_color: e.target.value })}
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
              placeholder="#7c3aed"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Aktiv: Text
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={settings.active_text_color}
              onChange={(e) => setSettings({ ...settings, active_text_color: e.target.value })}
              className="h-10 w-14 cursor-pointer rounded border-0"
            />
            <input
              type="text"
              value={settings.active_text_color}
              onChange={(e) => setSettings({ ...settings, active_text_color: e.target.value })}
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
              placeholder="#ffffff"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Hover: Hintergrund
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={settings.hover_bg_color}
              onChange={(e) => setSettings({ ...settings, hover_bg_color: e.target.value })}
              className="h-10 w-14 cursor-pointer rounded border-0"
            />
            <input
              type="text"
              value={settings.hover_bg_color}
              onChange={(e) => setSettings({ ...settings, hover_bg_color: e.target.value })}
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
              placeholder="#ede9fe"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Hover: Text
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={settings.hover_text_color}
              onChange={(e) => setSettings({ ...settings, hover_text_color: e.target.value })}
              className="h-10 w-14 cursor-pointer rounded border-0"
            />
            <input
              type="text"
              value={settings.hover_text_color}
              onChange={(e) => setSettings({ ...settings, hover_text_color: e.target.value })}
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
              placeholder="#6d28d9"
            />
          </div>
        </div>
      </div>

      {/* Speichern */}
      <div className="flex justify-end">
        <button
          onClick={saveSettings}
          disabled={saving}
          className="rounded-full bg-slate-900 px-6 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {saving ? "Speichern..." : "Farben speichern"}
        </button>
      </div>

      {/* Hinweis */}
      <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-700">
        <strong>Hinweis:</strong> Die Farbeinstellungen werden sofort angewendet und für alle Benutzer sichtbar.
      </div>
    </div>
  );
}
